import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import millify from 'millify';
import { Virtuoso } from 'react-virtuoso';
import { faUserGroup, faXmark } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { Modal } from '@components/main-components/modals/Modal';
import { Tooltip } from '@components/main-components/tooltip/Tooltip';
import { UserRowSkeleton } from '@components/modals/shared/UserList/UserRow';
import { useAuth } from '@context/auth/base';
import { ModalContext } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import {
  ArtistCollectorMintsModalDocument,
  ArtistCollectorsMintInfoFragmentDoc,
  CollectorMintsRowFragmentDoc,
  getFragment,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import { NAV_LINKS } from '@utils/constants';
import { getSingularOrPlural } from '@utils/helpers';
import { EmptyUserStat } from '../EmptyState';
import { MAX_UNVIRTUALIZED_ITEMS, StyledScrollArea } from '../shared';
import { CollectorMintsRow } from './CollectorMintsRow';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ArtistCollectorsMintInfo on Artist {
    id
    name
    numCollectors
    numNfts
    userId
  }

  query ArtistCollectorMintsModal(
    $artistId: UUID!
    $pagination: ArtistCollectorCursorConnectionArgs
  ) {
    artist(id: $artistId) {
      id
      collectors(pagination: $pagination) {
        edges {
          node {
            id
            ...CollectorMintsRow
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

const collectorMintItemContent = (i: number, user: FragmentType<CollectorMintsRowFragmentDoc>) => {
  const { id } = getFragment(CollectorMintsRowFragmentDoc, user);

  return (
    <div key={`collector-mints-${id}-${i}`} className="px-6">
      <CollectorMintsRow collectorMintData={user} />
    </div>
  );
};

const QUERY_MAX_COUNT = 14;

export const CollectorMintsModal = ({
  artistData,
  /**
   * @default mints
   */
  variant = 'mints',
}: {
  artistData: FragmentType<ArtistCollectorsMintInfoFragmentDoc>;
  variant?: 'mints' | 'collectors';
}) => {
  const {
    id: artistId,
    name,
    numNfts,
    numCollectors,
    userId,
  } = getFragment(ArtistCollectorsMintInfoFragmentDoc, artistData);
  const { closeCancelModal } = ModalContext.useContainer();
  const [scrollParent, setScrollParent] = React.useState<HTMLDivElement | undefined>();
  const [isAtTop, setIsAtTop] = React.useState(true);

  const { authUser } = useAuth();
  const isOwner = authUser?.id === userId;
  const {
    orderedList: collectors,
    loadMoreNextPage,
    isLoading,
  } = useInfiniteQuery(ArtistCollectorMintsModalDocument, {
    filterQueryKey: {
      artistId,
    },
    getNextPageParam({ data: { artist } }) {
      return (
        artist?.collectors.pageInfo.hasNextPage && {
          after: artist.collectors.pageInfo.endCursor,
        }
      );
    },
    variables({ pageParam }) {
      return {
        artistId,
        pagination: {
          first: QUERY_MAX_COUNT,
          after: pageParam?.after,
          sort: { nftsCount: 'DESC' as const },
        },
      };
    },
    list({ artist }) {
      return artist?.collectors.edges.map(({ node }) => node);
    },
    uniq({ id }) {
      return id;
    },
    staleTime: 0,
  });
  const noCollectorMints = numCollectors === 0 && numNfts === 0 && collectors.length === 0;

  const LoadingFooter = React.useCallback(
    () =>
      isLoading ? (
        <div className="h-6 px-6 ">
          <UserRowSkeleton />
          <UserRowSkeleton />
          <UserRowSkeleton />
        </div>
      ) : (
        <div className="h-6" />
      ),
    [isLoading],
  );

  const initialItems = React.useMemo(
    () => collectors.slice(0, MAX_UNVIRTUALIZED_ITEMS),
    [collectors],
  );

  const virtualizedItems = React.useMemo(
    () => collectors.slice(MAX_UNVIRTUALIZED_ITEMS),
    [collectors],
  );

  const body = (
    <StyledScrollArea
      ref={r => {
        setScrollParent(r || undefined);
      }}
      empty={noCollectorMints}
    >
      {noCollectorMints && !isLoading && (
        <EmptyUserStat
          header={
            variant === 'mints' ? `${name} has no mints yet` : `${name} has no collectors yet`
          }
          subText={`When someone mints one of ${name}'s releases, they'll show up here`}
        />
      )}

      {!noCollectorMints && (
        <>
          {/* Virtuoso takes a while to render so only shows 1 user on intial mount this it to always show prefetched data and not have to wait for virtuoso  */}
          {initialItems.length > 0 &&
            initialItems.map((user, i) => collectorMintItemContent(i, user))}
          {virtualizedItems.length > 0 && (
            <Virtuoso
              data={virtualizedItems}
              itemContent={collectorMintItemContent}
              customScrollParent={scrollParent}
              endReached={loadMoreNextPage}
              components={{ Footer: LoadingFooter }}
              atTopStateChange={atTop => {
                setIsAtTop(atTop);
              }}
            />
          )}
        </>
      )}
    </StyledScrollArea>
  );

  const label =
    variant === 'mints'
      ? `${millify(numNfts)} minted`
      : `  ${millify(numCollectors)} 
  ${getSingularOrPlural({
    word: 'collector',
    count: numCollectors,
  })}`;

  const subText =
    variant === 'mints'
      ? `${millify(numCollectors)} ${getSingularOrPlural({
          word: 'collector',
          count: numCollectors,
        })}`
      : `${millify(numNfts)} minted`;

  return (
    <Modal
      header={
        <div
          className={clsx(
            'flex w-full items-start justify-between px-6  pb-4 pt-6  transition-shadow duration-300',
            !isAtTop && 'shadow-md',
          )}
        >
          <div className="flex flex-col gap-1.5">
            <h3 className="font-title text-title-l font-medium">{label}</h3>
            <div className="relative flex w-fit items-center">
              <h4 className="font-base text-base-l font-normal text-base600">{subText}</h4>
              {isOwner && (
                <span className="absolute -right-7">
                  <div className="hidden lg:block">
                    <Tooltip openOnClick={false}>
                      <AccessibleLink pathnameUri={NAV_LINKS.Community}>
                        <Tooltip.Trigger asChild className="cursor-pointer">
                          <IconButton
                            size={24}
                            iconSize={16}
                            icon={<FontAwesomeIcon icon={faUserGroup} />}
                          />
                        </Tooltip.Trigger>
                      </AccessibleLink>
                      <Tooltip.StyledContent>View My Community</Tooltip.StyledContent>
                    </Tooltip>
                  </div>
                  <div className="block lg:hidden">
                    <AccessibleLink pathnameUri={NAV_LINKS.Community}>
                      <IconButton
                        size={24}
                        iconSize={16}
                        icon={<FontAwesomeIcon icon={faUserGroup} />}
                      />
                    </AccessibleLink>
                  </div>
                </span>
              )}
            </div>
          </div>

          <IconButton
            icon={<FontAwesomeIcon icon={faXmark} size="lg" className="text-base500" />}
            onClick={closeCancelModal}
          />
        </div>
      }
      body={body}
      // we need this to make sure the scrollbar is at the edge of the modal
      containerStyles={{
        padding: 0,
      }}
      bodyStyles={{
        paddingX: 0,
        marginTop: 0,
      }}
    />
  );
};
